import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ZodError } from 'zod'

import { App } from '@/index'

import { appConfigSchema } from './src/stores/app-store-config/types'

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
})

const init = () => {
	const ibeConfig = document.querySelector('#ibeConfig')
	const appMount = document.getElementById('ibeContainer') as Element

	const root = createRoot(appMount)

	// Reset url hash
	if (window.location.hash) {
		window.location.hash = ''
	}

	const parsedProps = JSON.parse((ibeConfig as HTMLScriptElement).innerHTML) ?? '{}'

	async function enableMocking() {
		if (process.env.NODE_ENV !== 'development') {
			return
		}

		const { worker } = await import('./src/mocks/browser')

		return worker.start({
			onUnhandledRequest: (req) => {
				if (req.url.includes('Resources')) {
					// Ignore the request and let it bypass MSW
					return 'bypass'
				}

				return console.warn('Unhandled request:', req.url)
			},
		})
	}

	try {
		const props = appConfigSchema.parse(parsedProps)

		enableMocking().then(() => {
			root.render(
				<StrictMode>
					<QueryClientProvider client={queryClient}>
						<App {...props} />
						<ReactQueryDevtools />
					</QueryClientProvider>
				</StrictMode>
			)
		})
	} catch (error) {
		if (error instanceof ZodError) {
			console.error(error.errors)
			root.render(
				<pre>
					<code>{JSON.stringify(error.errors, null, 4)}</code>
				</pre>
			)
		} else {
			console.error('Unexpected error:', error)
		}
	}
}

document.addEventListener('DOMContentLoaded', init)
